import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { theme as defaultTheme } from "@mindlercare/ui-web";
import { useQuestionnaireTableData } from "api/useQuestionnaireQueries";
import { useImplementedFeature } from "core/selectors/app";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { useUserHasRole } from "../../../../hooks/userHooks";
import { getQuestionnaireStatus } from "../getQuestionnairesStatus";
import { useEffect } from "react";
import { prefetchPatientMessages } from "hooks/messages";
import { Column, Row } from "./card";

const StyledStatusContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledStatusText = styled.div<{
  color: keyof typeof defaultTheme.COLORS;
}>`
  font-size: ${({ theme }) => defaultTheme.FONT.SIZE.SMALL_MEDIUM};
  font-weight: ${({ theme }) => defaultTheme.FONT.WEIGHT.MEDIUM};
  color: ${({ theme, color }) => defaultTheme.COLORS[color]};
`;
type PatientActionsProps = {
  patient?: RouterOutput["patients"]["getByUserId"];
};
const PatientActions = ({ patient }: PatientActionsProps) => {
  const { t } = useTranslation();

  const isPsychologist = useUserHasRole("Psychologist");

  const [isMessagingEnabled] = useImplementedFeature("MESSAGING_TO_PATIENT");

  const [isReferralEnabled] = useImplementedFeature("REFERRAL_CERTIFICATE");

  const { data: patientQuestionnaireAnswersAndRequests } =
    useQuestionnaireTableData({
      patientUserId: patient?.userId,
    });

  const status = getQuestionnaireStatus(
    patientQuestionnaireAnswersAndRequests,
    t,
  );

  useEffect(() => {
    if (patient) {
      void prefetchPatientMessages(patient.userId);
    }
  }, [patient]);

  return patient ? (
    <Row>
      {isReferralEnabled && isPsychologist && (
        <Column
          icon="Add"
          title={t("CERTIFICATE_OR_REFERRAL")}
          content={t("REFERRAL_DESCRIPTION")}
          link={`/referral-form/patientUserId/${patient.userId}`}
          buttonText={t("CREATE")}
          dataTestId="certificateOrReferral"
        />
      )}

      {isMessagingEnabled && (
        <Column
          icon="Message"
          title={t("ACTION_CARD_MESSAGE_TITLE")}
          content={t("ACTION_CARD_MESSAGE_DESCRIPTION")}
          link={`/messages/${patient.userId}`}
          buttonText={t("OPEN_MESSAGES")}
          dataTestId="patientMessage"
        />
      )}
      <Column
        icon="CheckList"
        title={t("ACTION_CARD_QUESTIONNAIRE_TITLE")}
        content={t("ACTION_CARD_QUESTIONNAIRE_DESCRIPTION")}
        status={
          status && (
            <StyledStatusContainer>
              <StyledStatusText
                color={
                  status.intent === "NEW_RESPONSE" ? "BLUE_DARK" : "GRAY_DARK"
                }
              >
                {status.text}
              </StyledStatusText>
            </StyledStatusContainer>
          )
        }
        link={`/questionnaires/${patient.userId}`}
        buttonText={t("OPEN_QUESTIONNAIRES")}
        dataTestId="patientQuestionnaire"
      />
    </Row>
  ) : null;
};

export default PatientActions;
